import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Heart, Users, Award, Star,
  Leaf, Globe, Code2, Smartphone, Sparkles, Quote,
  CheckCircle, ArrowRight, Mail,
} from "lucide-react";
import MainLayout from "../layouts/MainLayout";
import SEO from "../components/SEO";
import { useSettings } from "../contexts/SettingsContext";
import { resolveAssetDisplayUrl } from "../lib/assetUrl";

/* ─── Data ─── */
const STATS = [
  { number: "500+", label: "Địa điểm du lịch", icon: MapPin, color: "text-primary", bg: "bg-primary/10" },
  { number: "10K+", label: "Du khách tin dùng", icon: Heart, color: "text-rose-600", bg: "bg-rose-100" },
  { number: "50+", label: "Đối tác địa phương", icon: Users, color: "text-blue-600", bg: "bg-blue-100" },
  { number: "5+", label: "Năm hoạt động", icon: Award, color: "text-amber-600", bg: "bg-amber-100" },
];

const VALUES = [
  {
    icon: MapPin,
    title: "Chính xác & tin cậy",
    desc: "Mọi thông tin địa điểm đều được kiểm duyệt bởi đội ngũ địa phương, đảm bảo độ chính xác cao nhất cho du khách.",
  },
  {
    icon: Heart,
    title: "Trải nghiệm cá nhân",
    desc: "Lưu địa điểm yêu thích, tạo hành trình riêng và chia sẻ với bạn bè — mỗi chuyến đi là một câu chuyện của bạn.",
  },
  {
    icon: Globe,
    title: "Cập nhật liên tục",
    desc: "Dữ liệu địa điểm được cập nhật theo thời gian thực từ đối tác và cộng đồng du khách, luôn mới và chính xác.",
  },
  {
    icon: Leaf,
    title: "Du lịch bền vững",
    desc: "Chúng tôi ưu tiên giới thiệu các địa điểm tôn trọng môi trường và cộng đồng địa phương tại Đà Lạt.",
  },
  {
    icon: Smartphone,
    title: "Dễ dùng mọi nơi",
    desc: "Giao diện tối ưu trên mọi thiết bị — điện thoại, máy tính bảng hay laptop, trải nghiệm mượt mà như nhau.",
  },
  {
    icon: Code2,
    title: "Công nghệ hiện đại",
    desc: "Bản đồ tương tác, tìm kiếm thông minh và hệ thống gợi ý giúp bạn khám phá Đà Lạt nhanh chóng và hiệu quả.",
  },
];

const TEAM = [
  {
    name: "Nguyễn Thanh Nhân",
    role: "Founder & Lead Developer",
    avatar: "NT",
    color: "from-primary to-primary-hover",
    bio: "Yêu Đà Lạt từ năm 2018, dành 5 năm xây dựng nền tảng khám phá du lịch này với tâm huyết đưa Đà Lạt đến gần hơn với du khách.",
  },
  {
    name: "Trần Minh Khoa",
    role: "Content & Local Expert",
    avatar: "MK",
    color: "from-amber-600 to-orange-500",
    bio: "Người Đà Lạt gốc, hiểu rõ từng ngóc ngách. Chịu trách nhiệm kiểm duyệt và cập nhật nội dung địa điểm.",
  },
  {
    name: "Lê Thị Hương",
    role: "UX Designer",
    avatar: "TH",
    color: "from-rose-500 to-pink-500",
    bio: "Thiết kế giao diện thân thiện, đặt trải nghiệm người dùng lên hàng đầu trong từng chi tiết nhỏ nhất.",
  },
];

const TIMELINE = [
  { year: "2018", title: "Ý tưởng ra đời", desc: "Sau nhiều chuyến đi Đà Lạt không có bản đồ tốt, chúng tôi quyết định tự xây dựng một nền tảng riêng." },
  { year: "2019", title: "Ra mắt phiên bản đầu", desc: "Version 1.0 với 50 địa điểm, bản đồ đơn giản và giao diện mobile-first." },
  { year: "2021", title: "Mở rộng cộng đồng", desc: "Đạt 1.000 người dùng đầu tiên. Tích hợp tính năng yêu thích và đánh giá từ cộng đồng." },
  { year: "2023", title: "Bản đồ tương tác 2.0", desc: "Ra mắt bản đồ Leaflet tương tác, thêm danh mục ẩm thực và lưu trú, tích hợp AI gợi ý." },
  { year: "2024", title: "500+ địa điểm", desc: "Đạt mốc 500 địa điểm được kiểm duyệt, phủ toàn bộ thành phố Đà Lạt và vùng phụ cận." },
];

const TESTIMONIALS = [
  {
    name: "Thu Hà — Hà Nội",
    avatar: "TH",
    stars: 5,
    text: "Chuyến đi Đà Lạt của mình hoàn hảo hơn rất nhiều nhờ bản đồ này. Tìm được bao nhiêu quán cà phê xinh mà trước giờ không biết!",
  },
  {
    name: "Minh Đức — TP.HCM",
    avatar: "MĐ",
    stars: 5,
    text: "Thông tin địa điểm rất chi tiết và chính xác. Tôi dùng app này lên kế hoạch cho cả gia đình 6 người, rất thuận tiện.",
  },
  {
    name: "Jenny Nguyễn — Overseas",
    avatar: "JN",
    stars: 5,
    text: "Coming back to Đà Lạt after 10 years, this map helped me rediscover the city I love. Great content in Vietnamese!",
  },
];

const ICONS = {
  mapPin: MapPin,
  heart: Heart,
  users: Users,
  award: Award,
  leaf: Leaf,
  globe: Globe,
  code2: Code2,
  smartphone: Smartphone,
  sparkles: Sparkles,
  mail: Mail,
};

const About = () => {
  const { settings } = useSettings();
  const about = settings?.aboutPage;

  const heroTitle = (about?.hero?.title || "").trim() || "Về Lòng Vòng Đà Lạt";
  const heroSubtitle =
    (about?.hero?.subtitle || "").trim() ||
    "Góc nhìn địa phương — gợi ý tinh gọn để bạn đi Đà Lạt dễ hơn.";
  const heroBg = resolveAssetDisplayUrl(about?.hero?.background?.url, about?.hero?.background?.key);
  const aboutBgFallback =
    "https://images.unsplash.com/photo-1557750255-2f8c9993f4b4?auto=format&fit=crop&w=2400&q=70";
  const aboutHeroBg = heroBg || aboutBgFallback;

  const storyText = (about?.storySection?.story || "").trim();

  const statsData = (Array.isArray(about?.stats) && about.stats.length > 0)
    ? about.stats
    : null;
  const teamData = (Array.isArray(about?.team) && about.team.length > 0)
    ? about.team
    : null;
  const timelineData = (Array.isArray(about?.timeline) && about.timeline.length > 0)
    ? about.timeline
    : null;

  const valuesData = (Array.isArray(about?.values) && about.values.length > 0)
    ? about.values.map((v, idx) => {
        const fallback = VALUES[idx % VALUES.length];
        const Icon = ICONS[v.icon] || fallback.icon;
        return {
          icon: Icon,
          title: (v.title || "").trim() || fallback.title,
          desc: (v.desc || "").trim() || fallback.desc,
        };
      })
    : VALUES;

  const testimonialsData = (Array.isArray(about?.testimonials) && about.testimonials.length > 0)
    ? about.testimonials.map((t, idx) => {
        const fallback = TESTIMONIALS[idx % TESTIMONIALS.length];
        return {
          name: (t.name || "").trim() || fallback.name,
          avatar: (t.avatar || "").trim() || fallback.avatar,
          stars: Number(t.stars) || fallback.stars,
          text: (t.text || "").trim() || fallback.text,
        };
      })
    : TESTIMONIALS;

  const titleLines = heroTitle.split("\n");

  return (
  <MainLayout>
    <SEO
      title={(about?.seo?.title || "").trim() || "Về Lòng Vòng Đà Lạt"}
      description={(about?.seo?.description || "").trim() || "Tìm hiểu câu chuyện, sứ mệnh và đội ngũ đứng sau Lòng Vòng Đà Lạt."}
      url={(about?.seo?.url || "").trim() || "https://longvongdalat.com/__preview/about"}
    />
    <div className="bg-[#f6f1e7]">

      {/* ── Hero ── */}
      <section id="about-hero" className="relative overflow-hidden bg-gradient-to-br from-[#2b3a2b] via-[#2f4a33] to-[#5b5a3a] px-4 py-20 md:py-28">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              `linear-gradient(135deg, rgba(43,58,43,0.82) 0%, rgba(47,74,51,0.62) 45%, rgba(91,90,58,0.42) 100%), url("${aboutHeroBg}")`,
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/20" aria-hidden />
        {/* warm paper wash */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.55] mix-blend-soft-light"
          style={{
            backgroundImage:
              "radial-gradient(900px 520px at 16% 18%, rgba(255, 244, 214, 0.55), transparent 60%), radial-gradient(760px 460px at 82% 34%, rgba(255, 235, 205, 0.35), transparent 62%)",
          }}
          aria-hidden
        />
        {/* subtle grain */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 3px)",
          }}
          aria-hidden
        />
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#f3e2c0]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-0 h-64 w-64 rounded-full bg-[#f3e2c0]/10 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl text-center text-white">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/20 bg-white/10 backdrop-blur-sm">
            <MapPin size={36} />
          </div>
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-white/70">
            {(about?.hero?.kicker || "").trim() || "Về chúng tôi"}
          </p>
          <h1 className="font-faustina text-5xl italic font-bold leading-tight md:text-7xl">
            {titleLines.map((line, i) => (
              <React.Fragment key={i}>{line}{i < titleLines.length - 1 && <br />}</React.Fragment>
            ))}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/85">
            {heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to={(about?.hero?.primaryButtonTo || "").trim() || "/locations"}
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#1a3d14] shadow-lg hover:shadow-xl transition">
              <MapPin size={15} />
              {(about?.hero?.primaryButtonLabel || "").trim() || "Khám phá khu vực"}
            </Link>
            <Link to={(about?.hero?.secondaryButtonTo || "").trim() || "/#home-hero"}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
              {(about?.hero?.secondaryButtonLabel || "").trim() || "Xem bản đồ"}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="relative z-10 mx-auto -mt-8 max-w-5xl px-4 md:px-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {(statsData || STATS).map((item, idx) => {
            const isDefault = !statsData;
            const number = isDefault ? item.number : item.value;
            const label  = isDefault ? item.label  : item.label;
            const Icon   = isDefault ? item.icon   : STATS[idx % STATS.length]?.icon || MapPin;
            const color  = isDefault ? item.color  : STATS[idx % STATS.length]?.color || "text-primary";
            const bg     = isDefault ? item.bg     : STATS[idx % STATS.length]?.bg    || "bg-primary/10";
            return (
              <div key={idx}
                className="rounded-2xl border border-white/60 bg-white px-5 py-5 text-center shadow-[0_16px_48px_rgba(0,0,0,0.06)] hover:shadow-[0_24px_64px_rgba(0,0,0,0.09)] transition">
                <div className={`mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl ${bg}`}>
                  <Icon size={22} className={color} />
                </div>
                <p className={`text-3xl font-extrabold ${color}`}>{number}</p>
                <p className="mt-1 text-xs font-medium text-gray-500">{label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Story ── */}
      <section id="about-story" className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-20">
        <div className="overflow-hidden rounded-[2.5rem] border border-white/60 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.07)]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[400px] lg:min-h-[500px]">
              <img
                src={(about?.storySection?.image?.url || "").trim() || "https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=1400&q=80"}
                alt={(about?.storySection?.image?.alt || "").trim() || "Đà Lạt"}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-primary-hover/70 to-primary/30" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="rounded-2xl border border-white/20 bg-black/30 px-5 py-4 backdrop-blur-md">
                  <p className="text-sm font-bold text-white">
                    {(about?.storySection?.locationBadgeTitle || "").trim() || "🌿 Đà Lạt — Thành phố ngàn hoa"}
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    {(about?.storySection?.locationBadgeDesc || "").trim() || "Cao nguyên Lâm Viên, 1.500m so với mực nước biển"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-center p-8 md:p-12 lg:p-14">
              <p className="mb-3 text-xs uppercase tracking-[0.24em] text-primary/60">
                {(about?.storySection?.kicker || "").trim() || "Câu chuyện của chúng tôi"}
              </p>
              <h2 className="text-4xl font-bold text-primary">
                {(about?.storySection?.title || "").trim() || "Đam mê — Khám phá — Chia sẻ"}
              </h2>
              {storyText ? (
                storyText.split("\n\n").map((para, i) => (
                  <p key={i} className="mt-5 text-base leading-8 text-gray-600">{para}</p>
                ))
              ) : (
                <>
                  <p className="mt-5 text-base leading-8 text-gray-600">
                    Bắt đầu từ tình yêu với Đà Lạt, chúng tôi đã dành nhiều năm để khám phá từng ngõ ngách, từng địa điểm độc đáo và trải nghiệm những điều tuyệt vời nhất mà thành phố này mang lại.
                  </p>
                  <p className="mt-3 text-base leading-8 text-gray-600">
                    Với mong muốn chia sẻ những khám phá đó, chúng tôi tạo ra nền tảng bản đồ du lịch chi tiết nhất — giúp du khách tìm đúng nơi, đến đúng lúc, và có những kỷ niệm đẹp nhất.
                  </p>
                </>
              )}
              <div className="mt-6 space-y-2.5">
                {(
                  Array.isArray(about?.storySection?.checklist) && about.storySection.checklist.length > 0
                    ? about.storySection.checklist
                    : ["Kiểm duyệt thông tin thủ công từng địa điểm", "Cập nhật giờ mở cửa, giá cả định kỳ", "Phản hồi cộng đồng trong 24 giờ"]
                ).map((t) => (
                  <div key={t} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <CheckCircle size={16} className="shrink-0 text-primary" />{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section id="about-values" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-xs uppercase tracking-[0.24em] text-primary/60">
              {(about?.valuesSection?.kicker || "").trim() || "Giá trị cốt lõi"}
            </p>
            <h2 className="text-4xl font-bold text-primary">
              {(about?.valuesSection?.title || "").trim() || "Tại sao chọn chúng tôi?"}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {valuesData.map(({ icon: Icon, title, desc }) => (
              <div key={title}
                className="group rounded-2xl border border-gray-100 bg-[#fafaf7] p-6 transition hover:border-primary/20 hover:shadow-md">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 transition group-hover:bg-primary/15">
                  <Icon size={22} className="text-primary" />
                </div>
                <h3 className="mb-2.5 text-base font-bold text-primary">{title}</h3>
                <p className="text-sm leading-7 text-gray-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section id="about-timeline" className="mx-auto max-w-4xl px-4 md:px-8 py-16 md:py-20">
        <div className="mb-12 text-center">
          <p className="mb-2 text-xs uppercase tracking-[0.24em] text-primary/60">
            {(about?.timelineSection?.kicker || "").trim() || "Hành trình"}
          </p>
          <h2 className="text-4xl font-bold text-primary">
            {(about?.timelineSection?.title || "").trim() || "Lịch sử phát triển"}
          </h2>
        </div>
        <div className="relative">
          <div className="absolute left-[19px] top-0 h-full w-0.5 bg-gradient-to-b from-primary/40 to-transparent md:left-1/2 md:-ml-px" />
          <div className="space-y-8">
            {(timelineData || TIMELINE).map(({ year, title, desc }, i) => (
              <div key={year} className={`relative flex gap-6 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                {/* Dot */}
                <div className="absolute left-0 top-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-white font-bold text-[10px] text-primary shadow-sm md:static md:self-start">
                  {year.slice(2)}
                </div>
                {/* Content */}
                <div className={`ml-14 flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:ml-0 ${i % 2 === 0 ? "md:mr-8" : "md:ml-8"}`}>
                  <p className="text-xs font-bold uppercase tracking-widest text-primary/60">{year}</p>
                  <h3 className="mt-1 font-bold text-gray-800">{title}</h3>
                  <p className="mt-1 text-sm leading-6 text-gray-600">{desc}</p>
                </div>
                {i % 2 === 0 && <div className="hidden flex-1 md:block" />}
                {i % 2 !== 0 && <div className="hidden flex-1 md:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section id="about-team" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mb-12 text-center">
            <p className="mb-2 text-xs uppercase tracking-[0.24em] text-primary/60">
              {(about?.teamSection?.kicker || "").trim() || "Đội ngũ"}
            </p>
            <h2 className="text-4xl font-bold text-primary">
              {(about?.teamSection?.title || "").trim() || "Những người đứng sau dự án"}
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(teamData || TEAM).map((member, idx) => {
              const isDefault = !teamData;
              const name   = member.name;
              const role   = member.role;
              const bio    = member.bio;
              const avatar = isDefault ? member.avatar : (name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase());
              const color  = isDefault ? member.color  : (TEAM[idx % TEAM.length]?.color || "from-primary to-primary-hover");
              return (
                <div key={idx} className="overflow-hidden rounded-2xl border border-gray-100 bg-[#fafaf7] shadow-sm hover:shadow-md transition">
                  <div className={`flex items-center gap-4 bg-gradient-to-br ${color} px-6 py-6 text-white`}>
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-lg font-bold">
                      {avatar}
                    </div>
                    <div>
                      <p className="font-bold leading-snug">{name}</p>
                      <p className="text-xs text-white/70">{role}</p>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-sm leading-7 text-gray-600">{bio}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section id="about-testimonials" className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-20">
        <div className="mb-10 text-center">
          <p className="mb-2 text-xs uppercase tracking-[0.24em] text-primary/60">
            {(about?.testimonialsSection?.kicker || "").trim() || "Đánh giá"}
          </p>
          <h2 className="text-4xl font-bold text-primary">
            {(about?.testimonialsSection?.title || "").trim() || "Du khách nói gì?"}
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {testimonialsData.map(({ name, avatar, stars, text }) => (
            <div key={name} className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <Quote size={28} className="mb-3 text-primary/20" />
              <p className="flex-1 text-sm leading-7 text-gray-600">{text}</p>
              <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {avatar}
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{name}</p>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: stars }).map((_, i) => (
                    <Star key={i} size={13} fill="currentColor" className="text-amber-400" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Mission CTA ── */}
      <section className="bg-gradient-to-br from-[#2b3a2b] via-[#2f4a33] to-[#5b5a3a] px-4 py-16 md:py-20 text-center text-white">
        <Sparkles size={32} className="mx-auto mb-4 text-white/60" />
        <p className="mb-2 text-xs uppercase tracking-[0.24em] text-white/70">
          {(about?.missionCta?.kicker || "").trim() || "Liên hệ"}
        </p>
        <h2 className="text-3xl font-bold md:text-4xl">
          {(about?.missionCta?.title || "").trim() || "Bạn muốn góp ý để trang tốt hơn?"}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-white/85">
          {(about?.missionCta?.desc || "").trim() ||
            "Giúp mọi chuyến đi Đà Lạt trở nên dễ lên kế hoạch, dễ khám phá và đáng nhớ hơn."}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={(about?.missionCta?.primaryTo || "").trim() || "/"}
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#1a3d14] shadow-lg hover:shadow-xl transition">
            <Mail size={15} />
            {(about?.missionCta?.primaryLabel || "").trim() || "Liên hệ chúng tôi"}
          </Link>
          <Link to={(about?.missionCta?.secondaryTo || "").trim() || "/locations"}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
            {(about?.missionCta?.secondaryLabel || "").trim() || "Khám phá khu vực"} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  </MainLayout>
  );
};

export default About;
